import { useState } from 'react';
import { ColorLens as ColorLensIcon, Delete as DeleteIcon, Refresh as RefreshIcon, Save as SaveIcon } from '@mui/icons-material';
import { Box, Button, Dialog, DialogActions, DialogContent, DialogTitle, IconButton, List, ListItem, ListItemButton, ListItemText, Modal, Paper, Stack, TextField, Tooltip, Typography, useTheme } from '@mui/material';
import { ITheme } from 'theme/types';
import { ColorSelectorProps } from './types';

const ColorSelector = <T extends Record<string, string>>({ config }: ColorSelectorProps<T>) => {
  const theme = useTheme() as unknown as ITheme;
  const {
    colorPalette,
    presets,
    savePresetOpen,
    presetName,
    setPresetName,
    handleColorChange,
    handleReset,
    handleSavePreset,
    handleOpenSavePreset,
    handleCloseSavePreset,
    handleApplyPreset,
    handleDeletePreset,
  } = config.useHook();

  const [uncontrolledOpen, setUncontrolledOpen] = useState(false);
  const isModalControlled = config.modalOpen !== undefined && config.onModalOpenChange !== undefined;
  const open = isModalControlled ? Boolean(config.modalOpen) : uncontrolledOpen;
  const setModalOpen = (next: boolean) => {
    if (isModalControlled) {
      config.onModalOpenChange?.(next);
    } else {
      setUncontrolledOpen(next);
    }
  };

  const handleClose = () => {
    setModalOpen(false);
    if (config.onClose) {
      config.onClose();
    }
  };

  const onDeletePreset = (name: string, e: React.MouseEvent) => {
    e.stopPropagation();
    handleDeletePreset(name);
  };

  return (
    <>
      {!config.hideTrigger && (
        <Tooltip title={config.tooltip} arrow>
          <IconButton onClick={() => setModalOpen(true)} sx={{ color: 'primary.main', fontSize: 20 }}>
            <ColorLensIcon />
          </IconButton>
        </Tooltip>
      )}
      <Modal
        open={open}
        onClose={handleClose}
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
        }}
      >
        <Paper
          sx={{
            display: 'flex',
            flexDirection: 'column',
            gap: 2,
            p: 3,
            width: config.modalWidth || '600px',
            maxWidth: '800px',
            maxHeight: '90vh',
            overflow: 'auto',
            backgroundColor: 'background.paper',
            borderRadius: 2,
            ...(config.modalWidth && { margin: '0 auto' }),
          }}
        >
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <Typography variant="h6" color="primary">
              {config.title}
            </Typography>
            <Box sx={{ display: 'flex', gap: 1 }}>
              <Button
                startIcon={<RefreshIcon />}
                onClick={handleReset}
                size="small"
                variant="outlined"
              >
                Reset
              </Button>
            </Box>
          </Box>
          {presets.length > 0 && (
            <Box>
              <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 600 }}>
                Saved Presets
              </Typography>
              <List dense sx={{ border: '1px solid', borderColor: 'divider', borderRadius: 1, maxHeight: 200, overflow: 'auto' }}>
                {presets.map(preset => (
                  <ListItem
                    key={preset.name}
                    disablePadding
                    secondaryAction={
                      <IconButton
                        edge="end"
                        size="small"
                        onClick={e => onDeletePreset(preset.name, e)}
                        sx={{ mr: 1 }}
                      >
                        <DeleteIcon fontSize="small" />
                      </IconButton>
                    }
                  >
                    <ListItemButton onClick={() => handleApplyPreset(preset.name)}>
                      <ListItemText primary={preset.name} />
                    </ListItemButton>
                  </ListItem>
                ))}
              </List>
            </Box>
          )}
          <Button
            startIcon={<SaveIcon />}
            onClick={handleOpenSavePreset}
            size="small"
            variant="outlined"
          >
            Save Preset
          </Button>
          {config.sections.map(section => (
            <Box key={section.title}>
              <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 600 }}>
                {section.title}
              </Typography>
              <Stack spacing={1}>
                {section.keys.map(key => {
                  const swatchColor = colorPalette[key] ?? theme.palette.white ?? '#FFFFFF';
                  const swatchTextColor = theme.palette.getContrastText(swatchColor);

                  return (
                    <Box key={String(key)} sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 2 }}>
                      <Box
                        sx={{
                          width: section.keys.length > 6 ? 200 : 120,
                          height: 40,
                          backgroundColor: swatchColor,
                          border: '1px solid',
                          borderColor: 'divider',
                          borderRadius: 1,
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          ...(section.keys.length > 6 && { textAlign: 'center' }),
                        }}
                      >
                        <Typography
                          variant="caption"
                          sx={{
                            color: swatchTextColor,
                            fontWeight: 600,
                          }}
                        >
                          {config.statusLabels[key]}
                        </Typography>
                      </Box>
                      <TextField
                        type="color"
                        value={config.toSolidColor(swatchColor)}
                        onChange={e => handleColorChange(key, e.target.value)}
                        size="small"
                        sx={{ width: 80 }}
                      />
                      <TextField
                        value={swatchColor}
                        disabled
                        size="small"
                        sx={{ width: 120 }}
                        placeholder="#hexcolor"
                      />
                    </Box>
                  );
                })}
              </Stack>
            </Box>
          ))}
          {config.showPreview && config.previewComponent && (
            <Box>
              <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 600 }}>
                Preview
              </Typography>
              {config.previewComponent(colorPalette)}
            </Box>
          )}

          <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 1 }}>
            <Button onClick={handleClose} variant="contained">
              Done
            </Button>
          </Box>
        </Paper>
      </Modal>
      <Dialog open={savePresetOpen} onClose={handleCloseSavePreset}>
        <DialogTitle>Save Color Palette Preset</DialogTitle>
        <DialogContent>
          <TextField
            autoFocus
            margin="dense"
            label="Preset Name"
            fullWidth
            variant="outlined"
            value={presetName}
            onChange={e => setPresetName(e.target.value)}
            onKeyDown={e => {
              if (e.key === 'Enter' && presetName.trim()) {
                handleSavePreset();
              }
            }}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseSavePreset}>Cancel</Button>
          <Button onClick={handleSavePreset} variant="contained" disabled={!presetName.trim()}>
            Save
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default ColorSelector;

